import type { Permission, Role } from './types';
import { PERMISSIONS, ROLES } from './types';

/**
 * RBAC 矩阵 —— 唯一事实来源。
 *
 * 前端用它隐藏/禁用 UI（体验层），Worker 用它强制鉴权（安全层）。
 * 任何权限判断都必须走 `hasPermission` / `hasAnyPermission`，
 * 不要在业务代码里手写 `role === 'admin'` 之类的比较。
 *
 * 这个平台有意只保留三层：成员自己提创意、评审员推动立项与上线、管理员管人管设置。
 * 没有审批链，也没有上下级汇报关系。
 */
export const ROLE_PERMISSIONS: Readonly<Record<Role, readonly Permission[]>> = {
  /** 成员：可提创意、参与讨论、发起 AI 研判；只能改自己的东西 */
  member: [
    'idea.create',
    'idea.view',
    'idea.edit.own',
    'idea.transition.own',
    'comment.create',
    'comment.delete.own',
    'ai.run',
    'ai.view',
  ],

  /** 评审员：在成员之上，可推动状态流转、指派负责人、删任意评论 */
  reviewer: [
    'idea.create',
    'idea.view',
    'idea.edit.own',
    'idea.edit.any',
    'idea.transition.own',
    'idea.transition.review',
    'idea.assign',
    'comment.create',
    'comment.delete.own',
    'comment.delete.any',
    'ai.run',
    'ai.view',
  ],

  /** 管理员：全部权限，含用户管理、平台设置、审计与资源用量查看 */
  admin: PERMISSIONS,
};

export const ROLE_LABELS: Readonly<Record<Role, string>> = {
  member: '成员',
  reviewer: '评审员',
  admin: '管理员',
};

export const ROLE_DESCRIPTIONS: Readonly<Record<Role, string>> = {
  member: '可以提出创意、参与讨论、发起 AI 研判，并维护自己提交的内容。',
  reviewer: '在成员基础上可推动创意立项与上线、指派负责人，并管理全部讨论内容。',
  admin: '拥有平台全部权限，可管理账号、分类标签、AI 设置与审计日志。',
};

export const PERMISSION_LABELS: Readonly<Record<Permission, string>> = {
  'idea.create': '提出创意',
  'idea.view': '查看创意',
  'idea.edit.own': '编辑自己的创意',
  'idea.edit.any': '编辑任意创意',
  'idea.transition.own': '流转自己创意的状态',
  'idea.transition.review': '评审流转（立项 / 上线）',
  'idea.assign': '指派负责人',
  'comment.create': '发表评论',
  'comment.delete.own': '删除自己的评论',
  'comment.delete.any': '删除任意评论',
  'ai.run': '发起 AI 研判',
  'ai.view': '查看 AI 研判结果',
  'user.view': '查看用户',
  'user.manage': '管理用户',
  'settings.manage': '管理平台设置',
  'audit.view': '查看审计日志',
  'usage.view': '查看资源用量',
};

export function isRole(value: unknown): value is Role {
  return typeof value === 'string' && (ROLES as readonly string[]).includes(value);
}

export function isPermission(value: unknown): value is Permission {
  return typeof value === 'string' && (PERMISSIONS as readonly string[]).includes(value);
}

/** 列出某角色的全部权限。 */
export function permissionsForRole(role: Role): Permission[] {
  return [...(ROLE_PERMISSIONS[role] ?? [])];
}

/** 单一权限判定。未知角色一律拒绝。 */
export function hasPermission(role: Role | null | undefined, permission: Permission): boolean {
  if (!role) return false;
  const granted = ROLE_PERMISSIONS[role];
  if (!granted) return false;
  return granted.includes(permission);
}

/** 任一权限命中即通过。 */
export function hasAnyPermission(role: Role | null | undefined, permissions: readonly Permission[]): boolean {
  return permissions.some((permission) => hasPermission(role, permission));
}

/** 全部权限命中才通过。 */
export function hasAllPermissions(role: Role | null | undefined, permissions: readonly Permission[]): boolean {
  return permissions.every((permission) => hasPermission(role, permission));
}
